import { DEFAULT_COLOR } from "../config/constants";
import type { LegacyFormField, LegacyFormFieldValue } from "../types";

const HEX_COLOR_PATTERN = /^#[0-9a-f]{6}$/i;

export type FieldState = Record<string, LegacyFormFieldValue>;

export function stringifyFieldValue(value: LegacyFormFieldValue): string {
  if (value == null) {
    return "";
  }

  return Array.isArray(value) ? value.join(",") : String(value);
}

export function appendFieldValue(formData: FormData, name: string, value: LegacyFormFieldValue): void {
  if (Array.isArray(value)) {
    value.forEach((item) => formData.append(name, String(item)));
    return;
  }

  formData.append(name, typeof value === "boolean" ? String(value) : stringifyFieldValue(value));
}

export function appendExternalFormFields(formData: FormData, formId?: string): void {
  if (!formId) {
    return;
  }

  const selector = `input[form='${formId}'], textarea[form='${formId}'], select[form='${formId}']`;
  document.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(selector).forEach((field) => {
    if (!field.name || field.disabled) {
      return;
    }

    const value = field instanceof HTMLInputElement && field.type === "checkbox" ? String(field.checked) : field.value;
    formData.append(field.name, value);
  });
}

export function normalizeColor(value: string): string {
  return HEX_COLOR_PATTERN.test(value) ? value : DEFAULT_COLOR;
}

export function toStringArray(value: LegacyFormFieldValue): string[] {
  if (Array.isArray(value)) {
    return value.map(String);
  }

  return value == null || value === "" ? [] : [String(value)];
}

export function getInitialFieldValue(field: LegacyFormField): LegacyFormFieldValue {
  if (field.type === "color") {
    return normalizeColor(stringifyFieldValue(field.value));
  }

  if (field.type === "odata-multiselect" && toStringArray(field.value).length === 0 && field.selectedOptions?.length) {
    return field.selectedOptions.map((option) => String(option.value));
  }

  return field.value;
}

export function getDisplayText(field: LegacyFormField, type: string, value: LegacyFormFieldValue): string {
  if (type === "select") {
    const selectedValue = stringifyFieldValue(value);
    return field.options?.find((option) => option.value === selectedValue)?.text ?? selectedValue;
  }

  if (type === "odata-multiselect") {
    const optionsByValue = new Map(
      (field.selectedOptions ?? field.options ?? []).map((option) => [String(option.value), String(option.text)])
    );
    return toStringArray(value)
      .map((item) => optionsByValue.get(item) ?? item)
      .join(", ");
  }

  return stringifyFieldValue(value);
}

export function shouldDisplayReadOnly(field: LegacyFormField, type: string, canSubmit: boolean): boolean {
  const interactiveTypes = new Set(["checkbox", "link-button", "hidden", "segmented-radio"]);
  return !interactiveTypes.has(type) && (Boolean(field.disabled) || !canSubmit);
}
